import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { isChunkLoadError, canReloadForChunkError, reloadForChunkError } from '@/lib/lazyWithRetry';
import { reportClientError, describeThrown } from '@adrodd/core';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isAutoReloading: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    isAutoReloading: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Show the reload spinner (not the error card) when a stale-chunk error can
    // still be auto-recovered. This is the backstop for chunk errors that slip
    // past lazyWithRetry (e.g. thrown from a non-lazy dynamic import).
    const autoReload = isChunkLoadError(error) && canReloadForChunkError();
    return { hasError: true, error, errorInfo: null, isAutoReloading: autoReload };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });

    // A stale-chunk error is a deploy artefact, not a bug — it auto-recovers on
    // reload, so reporting it would bury real crashes under redeploy noise.
    if (isChunkLoadError(error)) {
      reloadForChunkError();
      return;
    }

    reportClientError({
      ...describeThrown(error),
      where: window.location.pathname,
      fatal: true,
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, isAutoReloading: false });
    window.location.reload();
  };

  public render() {
    if (this.state.isAutoReloading) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-8 w-8 text-destructive" />
                <CardTitle className="text-2xl">Something went wrong</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                We hit a problem displaying this page. Reloading usually fixes it.
              </p>

              {/* Technical details are development-only — never leak raw error
                  strings (e.g. failed chunk URLs) or stack traces to end users. */}
              {process.env.NODE_ENV !== 'production' && this.state.error && (
                <div className="bg-muted p-4 rounded-md">
                  <p className="font-mono text-sm font-semibold mb-2">Error (development only):</p>
                  <p className="font-mono text-sm text-destructive break-all">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <details className="mt-2">
                      <summary className="font-mono text-sm font-semibold cursor-pointer mb-2">
                        Stack Trace
                      </summary>
                      <pre className="font-mono text-xs overflow-auto mt-2 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button onClick={this.handleReset}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reload Page
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.history.back()}
                >
                  Go Back
                </Button>
              </div>

              <p className="text-sm text-muted-foreground">
                If this problem persists, please contact support with the error details above.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
